import * as React from "react";
import Popover from "@mui/material/Popover";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import FlagCategoryPicker, { FlagOption } from "../../common/FlagCategoryPicker";
import TemplateFormDrawer from "./TemplateFormDrawer";
import {
  createTemplate,
  deleteTemplate,
  fetchFlags,
  fetchMilestones,
  fetchTemplates,
  previewTemplate,
  updateTemplate,
} from "./communicationTemplate.service";
import {
  AirCommunicationTemplateProps,
  DrawerState,
  Milestone,
  StatusFilter,
  TemplateRow,
} from "./communicationTemplate.types";
import SearchInput from "../../common/SearchInput";

const PAGE_SIZE = 10;

const emptyDrawerState: DrawerState = {
  id: null,
  templateName: "",
  waMilestones: [],
  emailMilestones: [],
  flags: [],
  isActive: true,
};


export default function CommunicationTemplate({ mode }: AirCommunicationTemplateProps) {
  const [rows, setRows] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [milestonedata, setMilestonedata] = React.useState<Milestone[]>([]);
  const [flags, setFlags] = React.useState<FlagOption[]>([]);

  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");
  const [currentPage, setCurrentPage] = React.useState(0);

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerState, setDrawerState] = React.useState<DrawerState>(emptyDrawerState);
  const [flagPickerOpen, setFlagPickerOpen] = React.useState(false);

  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewTab, setPreviewTab] = React.useState<"email" | "wa">("email");
  const [previewTitle, setPreviewTitle] = React.useState("");
  const [emailPreviewData, setEmailPreviewData] = React.useState<{ subject: string; html: string } | null>(null);
  const [whatsappPreviewData, setWhatsappPreviewData] = React.useState<string | null>(null);

  const [popover, setPopover] = React.useState<{ anchor: HTMLElement; content: React.ReactNode } | null>(null);

  // ── Data loading ─────────────────────────────────────────────────
  const getTemplatedata = React.useCallback(
    async (search: string, status: StatusFilter, currentMode: string) => {
      setLoading(true);
      try {
        const data = await fetchTemplates(search, status, currentMode);
        setRows(data);
      } catch (error) {
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Debounce search
  React.useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Re-fetch whenever search, status, OR mode changes (all backend-driven)
  React.useEffect(() => {
    getTemplatedata(debouncedSearch, statusFilter, mode);
  }, [debouncedSearch, statusFilter, mode, getTemplatedata]);

  React.useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, statusFilter, mode]);

  // Lookups — milestones are mode-based, flags are shared
  React.useEffect(() => {
    (async () => {
      setMilestonedata(await fetchMilestones(mode));
    })();
  }, [mode]);

  React.useEffect(() => {
    (async () => {
      setFlags(await fetchFlags());
    })();
  }, []);

  const refetch = () => getTemplatedata(debouncedSearch, statusFilter, mode);

  // ── Row mapping (server shape → view shape) ─────────────────────
  const mappedRows: TemplateRow[] = React.useMemo(
    () =>
      rows.map((row: any) => ({
        ...row,
        id: row?._id || row.id,
        templatename: row?.templateName,
        waMilestones: row?.waMilestones || [],
        emailMilestones: row?.emailMilestones || [],
        flags: row?.flagDetails || [],
        isActive: row?.isActive !== false,
      })),
    [rows]
  );

  // Backend already filters by status, so pagination runs on mappedRows directly.
  const pageCount = Math.max(1, Math.ceil(mappedRows.length / PAGE_SIZE));
  const pagedRows = mappedRows.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

  // ── Drawer (add / edit) ──────────────────────────────────────────
  const openAddDrawer = () => {
    setDrawerState(emptyDrawerState);
    setDrawerOpen(true);
  };

  const openEditDrawer = (row: TemplateRow) => {
    setDrawerState({
      id: row.id,
      templateName: row.templatename || "",
      waMilestones: row.waMilestones || [],
      emailMilestones: row.emailMilestones || [],
      flags: row.flags || [],
      isActive: row.isActive !== false,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => setDrawerOpen(false);

  const handleSaveTemplate = async () => {
    const formData = {
      templateName: drawerState.templateName,
      waMilestones: drawerState.waMilestones.map((m) => m?._id),
      emailMilestones: drawerState.emailMilestones.map((m) => m?._id),
      flagIds: drawerState.flags?.map((f: any) => f?._id) || [],
      isActive: drawerState.isActive,
    };

    try {
      if (!drawerState.id) {
        const response = await createTemplate(formData, mode);
        if (response.data.statusCode === 201) {
          toast.success(response.data.message);
          setCurrentPage(0);
          refetch();
          closeDrawer();
        }
      } else {
        const response = await updateTemplate(drawerState.id, formData, mode);
        const errorData = response.data?.error;
        if (typeof errorData === "object" && !Array.isArray(errorData) && errorData) {
          Object.keys(errorData).forEach((field) => toast.error(`${field}: ${errorData[field]}`));
        } else {
          toast.success("Template updated successfully");
          refetch();
          closeDrawer();
        }
      }
    } catch (err: any) {
      const errors = err?.response?.data?.error;
      if (Array.isArray(errors)) {
        errors.forEach((e: any) => toast.error(`${e.field}: ${e.message}`));
      } else if (typeof errors === "object" && errors) {
        Object.keys(errors).forEach((field) => toast.error(`${field}: ${errors[field]}`));
      } else if (typeof errors === "string") {
        toast.error(errors);
      } else {
        toast.error("An error occurred while saving the template.");
      }
    }
  };

  // No confirm popup — delete immediately
  const handleDeleteClick = async (row: TemplateRow) => {
    try {
      const response = await deleteTemplate(row.id, mode);
      refetch();
      if (response.status === 200) toast.success("Template deleted successfully.");
    } catch (error: any) {
      toast.error("An error occurred while deleting the template.");
    }
  };

  const handlePreviewClick = async (row: TemplateRow) => {
    try {
      const { data } = await previewTemplate(
        {
          emailMilestones: row.emailMilestones,
          waMilestones: row?.waMilestones,
        },
        mode
      );
      setEmailPreviewData({
        subject: data?.data?.EmailhtmlTemplete?.subject || "",
        html: data?.data?.EmailhtmlTemplete?.html || "",
      });
      setWhatsappPreviewData(data?.data?.whatsappHtmlTemplete || "");
      setPreviewTitle(row.templatename);
      setPreviewTab("email");
      setPreviewOpen(true);
    } catch (error) {
      console.error("Preview API error:", error);
      toast.error("Could not load preview.");
    }
  };

  // ── Popovers ──────────────────────────────────────────────────────
  const showMilestonePopover = (e: React.MouseEvent<HTMLElement>, channel: "wa" | "email", list: Milestone[]) => {
    const label = channel === "wa" ? "WhatsApp" : "Email";
    const icon = channel === "wa" ? "💬" : "✉️";
    setPopover({
      anchor: e.currentTarget,
      content: (
        <>
          <div className="ctpl-popover-title">{icon} {label} milestones ({list.length})</div>
          <ul className="ctpl-popover-list">
            {list.map((m: any, i: number) => (
              <li key={m?._id || i}>{m?.label || m?.milestone}</li>
            ))}
          </ul>
        </>
      ),
    });
  };

  const showFlagsPopover = (e: React.MouseEvent<HTMLElement>, flagList: FlagOption[]) => {
    setPopover({
      anchor: e.currentTarget,
      content: (
        <>
          <div className="ctpl-popover-title">🏷 All flags ({flagList.length})</div>
          <ul className="ctpl-popover-list">
            {flagList.map((f: any, i: number) => (
              <li key={f?._id || i}>{f?.category ? `${f.category}: ${f.name}` : f?.name}</li>
            ))}
          </ul>
        </>
      ),
    });
  };

  const renderFlags = (flagList: FlagOption[]) => {
    const shown = flagList.slice(0, 2);
    const extra = flagList.length - 2;
    return (
      <>
        {shown.map((f: any, i: number) => (
          <span className="ctpl-flag-chip" key={f?._id || i}>
            {f?.category ? `${f.category}: ${f.name}` : f?.name}
          </span>
        ))}
        {extra > 0 && (
          <button type="button" className="ctpl-flag-more" onClick={(e) => showFlagsPopover(e, flagList)}>
            +{extra}
          </button>
        )}
      </>
    );
  };

  const modeLabel = mode === "air" ? "Air" : "Ocean";

  return (
    <>
      <div className="ctpl-card">
        <div className="ctpl-toolbar">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search templates…"
          />

          <div className="ctpl-filter-chips">
            {(["all", "active", "inactive"] as const).map((f) => (
              <button
                key={f}
                type="button"
                className={`ctpl-filter-chip${statusFilter === f ? " active" : ""}`}
                onClick={() => setStatusFilter(f)}
              >
                {f === "all" ? "All" : f === "active" ? "Active" : "Inactive"}
              </button>
            ))}
          </div>

          <div className="ctpl-spacer" />

          <button className="ctpl-btn ctpl-btn-primary" onClick={openAddDrawer}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Template
          </button>
        </div>

        <div className="ctpl-table-scroll">
          <table className="ctpl-table">
            <thead>
              <tr>
                <th style={{ width: "26%" }}>Template</th>
                <th style={{ width: "20%" }}>Channels</th>
                <th style={{ width: "26%" }}>Flags</th>
                <th style={{ width: "12%" }}>Status</th>
                <th style={{ width: "16%", textAlign: "right", paddingRight: 22 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="ctpl-empty-cell" colSpan={5}>Loading templates…</td></tr>
              ) : pagedRows.length === 0 ? (
                <tr><td className="ctpl-empty-cell" colSpan={5}>No templates found</td></tr>
              ) : (
                pagedRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="ctpl-tmpl-name">{row.templatename}</div>
                      <div className="ctpl-tmpl-sub">{modeLabel}</div>
                    </td>
                    <td>
                      <div className="ctpl-channel-badges">
                        <button type="button" className="ctpl-channel-badge wa" onClick={(e) => showMilestonePopover(e, "wa", row.waMilestones)}>
                          💬 {row.waMilestones.length}
                        </button>
                        <button type="button" className="ctpl-channel-badge email" onClick={(e) => showMilestonePopover(e, "email", row.emailMilestones)}>
                          ✉️ {row.emailMilestones.length}
                        </button>
                      </div>
                    </td>
                    <td>{renderFlags(row.flags)}</td>
                    <td>
                      <span className={`ctpl-status-pill ${row.isActive ? "active" : "inactive"}`}>
                        <span className="ctpl-status-dot" />
                        {row.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div className="ctpl-row-actions">
                        <button
                          className="ctpl-action-circle preview"
                          title="Preview"
                          onClick={() => handlePreviewClick(row)}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                        <button
                          className="ctpl-action-circle edit"
                          title={row.isActive ? "Edit" : "Activate the template to edit"}
                          onClick={() => openEditDrawer(row)}
                          disabled={!row.isActive}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                          </svg>
                        </button>
                        <button
                          className="ctpl-action-circle delete"
                          title={row.isActive ? "Delete" : "Activate the template to delete"}
                          onClick={() => handleDeleteClick(row)}
                          disabled={!row.isActive}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0-1 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L6 6" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="ctpl-table-footer">
          <span>{mappedRows.length} template{mappedRows.length === 1 ? "" : "s"}</span>
          <div className="ctpl-pagination">
            <button className="ctpl-page-btn" disabled={currentPage === 0} onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}>‹</button>
            <span>Page {currentPage + 1} of {pageCount}</span>
            <button className="ctpl-page-btn" disabled={currentPage >= pageCount - 1} onClick={() => setCurrentPage((p) => Math.min(pageCount - 1, p + 1))}>›</button>
          </div>
        </div>
      </div>

      {/* ── Reusable add / edit drawer ─────────────────────────────── */}
      <TemplateFormDrawer
        open={drawerOpen}
        state={drawerState}
        milestoneOptions={milestonedata}
        onChange={setDrawerState}
        onClose={closeDrawer}
        onSave={handleSaveTemplate}
        onEditFlags={() => setFlagPickerOpen(true)}
      />

      <FlagCategoryPicker
        open={flagPickerOpen}
        onClose={() => setFlagPickerOpen(false)}
        flags={flags}
        value={drawerState.flags}
        selectionMode="single"
        categoryOrder={["Branch", "Job Type", "Mode"]}
        title="Tag Flags"
        onSave={(selected: any) => {
          setDrawerState((s) => ({ ...s, flags: selected }));
          setFlagPickerOpen(false);
        }}
      />

      {/* ── Preview modal ───────────────────────────────────────────── */}
      <div className={`ctpl-modal-backdrop${previewOpen ? " open" : ""}`} onClick={() => setPreviewOpen(false)}>
        <div className="ctpl-modal" onClick={(e) => e.stopPropagation()}>
          <div className="ctpl-modal-header">
            <h2>Preview — {previewTitle}</h2>
            <button className="ctpl-icon-btn" onClick={() => setPreviewOpen(false)}><CloseIcon fontSize="small" /></button>
          </div>
          <div className="ctpl-tabs">
            <button className={`ctpl-tab-btn${previewTab === "email" ? " active" : ""}`} onClick={() => setPreviewTab("email")}>✉️ Email</button>
            <button className={`ctpl-tab-btn${previewTab === "wa" ? " active" : ""}`} onClick={() => setPreviewTab("wa")}>💬 WhatsApp</button>
          </div>
          <div className={`ctpl-tab-panel${previewTab === "email" ? " active" : ""}`}>
            <div className="ctpl-email-frame">
              <div className="browser-bar"><span /><span /><span /></div>
              <div className="subject">{emailPreviewData?.subject || "No Subject"}</div>
              <div className="meta">Email preview</div>
              <div className="body" dangerouslySetInnerHTML={{ __html: emailPreviewData?.html || "<em>No content</em>" }} />
            </div>
          </div>
          <div className={`ctpl-tab-panel${previewTab === "wa" ? " active" : ""}`}>
            <div className="ctpl-phone-frame">
              <div className="ctpl-phone-screen">
                <div className="ctpl-bubble">
                  {whatsappPreviewData || "No content"}
                  <div className="ctpl-bubble-time">Preview</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Popover
        open={!!popover}
        anchorEl={popover?.anchor}
        onClose={() => setPopover(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ className: "ctpl-popover-paper" }}
      >
        {popover?.content}
      </Popover>
    </>
  );
}